/** The same closed condition grammar is used by the editor, preview and event worker. */
export type WorkflowCondition =
  | { kind: 'all' }
  | { kind: 'score'; operator: '>=' | '>' | '<=' | '<' | '=' | '!='; threshold: number }
  | { kind: 'scheduled_visit' }
  | { kind: 'overdue_24h' }
  | { kind: 'published_available' };

export function parseWorkflowCondition(value: unknown, trigger?: string): WorkflowCondition | null {
  if (typeof value !== 'string') return null;
  const condition = value.trim();
  if (condition === 'All matching records') return { kind: 'all' };
  if (condition === 'Every scheduled visit' && (!trigger || trigger === 'SiteVisitScheduled'))
    return { kind: 'scheduled_visit' };
  if (condition === 'Overdue by 24 hours' && (!trigger || trigger === 'TaskOverdue'))
    return { kind: 'overdue_24h' };
  if (
    condition === 'Published and available inventory' &&
    (!trigger || trigger === 'PropertyAvailabilityChanged')
  )
    return { kind: 'published_available' };
  const match = /^(?:BOS|score)\s*(>=|<=|!=|>|<|=|≥|≤)\s*(\d{1,3})$/i.exec(condition);
  if (!match || Number(match[2]) > 100 || trigger === 'PropertyAvailabilityChanged') return null;
  const operator = match[1] === '≥' ? '>=' : match[1] === '≤' ? '<=' : match[1];
  return {
    kind: 'score',
    operator: operator as Extract<WorkflowCondition, { kind: 'score' }>['operator'],
    threshold: Number(match[2]),
  };
}

export function workflowMatches(
  condition: WorkflowCondition,
  record: any,
  subject: any,
  now = Date.now(),
) {
  if (subject?.suppressed || subject?.privacyArchived) return false;
  if (condition.kind === 'all') return true;
  if (condition.kind === 'scheduled_visit')
    return ['Scheduled', 'Confirmed'].includes(record.status);
  if (condition.kind === 'overdue_24h')
    return (
      !record.completed && record.type !== 'Automation' && Date.parse(record.dueAt) < now - 86400000
    );
  if (condition.kind === 'published_available')
    return (
      record.qualityState === 'Published' && ['Available', 'Limited'].includes(record.availability)
    );
  const score = subject?.score;
  if (typeof score !== 'number' || !Number.isFinite(score) || score < 0 || score > 100)
    return false;
  switch (condition.operator) {
    case '>=':
      return score >= condition.threshold;
    case '>':
      return score > condition.threshold;
    case '<=':
      return score <= condition.threshold;
    case '<':
      return score < condition.threshold;
    case '=':
      return score === condition.threshold;
    case '!=':
      return score !== condition.threshold;
  }
}
